// src/polaris-components/ChangePaymentMethod.tsx
import React, { useState } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { getSetupIntent, confirmPaymentMethodUpdate } from '../utils/subscriptionApi';
import '../styles/change-payment-method.css';

interface ChangePaymentMethodProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ChangePaymentMethod: React.FC<ChangePaymentMethodProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [postalCode, setPostalCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError('Card element not found. Please refresh and try again.');
      return;
    }

    if (!cardNumberComplete || !cardExpiryComplete || !cardCvcComplete) {
      setError('Please complete all card details.');
      return;
    }

    if (!postalCode.trim()) {
      setError('Please enter a postal code.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { clientSecret } = await getSetupIntent();

      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              address: {
                postal_code: postalCode,
              },
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message || 'Failed to confirm card setup');
      }

      if (!setupIntent?.payment_method) {
        throw new Error('No payment method returned from Stripe');
      }

      await confirmPaymentMethodUpdate(setupIntent.payment_method as string);

      setSuccessMessage('Payment method updated successfully!');

      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);

    } catch (err: any) {
      setError(err.message || 'Failed to update payment method. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      setSuccessMessage(null);
      setCardNumberComplete(false);
      setCardExpiryComplete(false);
      setCardCvcComplete(false);
      setPostalCode('');
      onClose();
    }
  };

  const handleCardNumberChange = (event: any) => {
    setCardNumberComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleCardExpiryChange = (event: any) => {
    setCardExpiryComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleCardCvcChange = (event: any) => {
    setCardCvcComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPostalCode(e.target.value);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose} data-testid="change-payment-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Change Payment Method</h2>
          <button
            className="close-btn"
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close modal"
            data-testid="close-modal-btn"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="payment-form">
          <div className="form-content">
            <p className="form-description">
              Enter your new payment card details below. Your current payment method will be replaced.
            </p>

            {!stripe && (
              <div className="error-message" data-testid="stripe-loading-error">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 1L1 14H15L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 6V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="8" cy="11.5" r="0.5" fill="currentColor"/>
                </svg>
                Stripe is not loaded. Please check your configuration and refresh the page.
              </div>
            )}

            <div className="card-element-container">
              <label htmlFor="card-number-element" className="form-label">
                Card Number
              </label>
              <div className="card-element-wrapper">
                <CardNumberElement
                  id="card-number-element"
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#1a1a1a',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        '::placeholder': {
                          color: '#9ca3af',
                        },
                        iconColor: '#6b7280',
                      },
                      invalid: {
                        color: '#ef4444',
                        iconColor: '#ef4444',
                      },
                    },
                  }}
                  onChange={handleCardNumberChange}
                />
              </div>
            </div>

            <div className="card-row">
              <div className="card-element-container card-half">
                <label htmlFor="card-expiry-element" className="form-label">
                  Expiry Date
                </label>
                <div className="card-element-wrapper">
                  <CardExpiryElement
                    id="card-expiry-element"
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#1a1a1a',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          '::placeholder': {
                            color: '#9ca3af',
                          },
                        },
                        invalid: {
                          color: '#ef4444',
                        },
                      },
                    }}
                    onChange={handleCardExpiryChange}
                  />
                </div>
              </div>

              <div className="card-element-container card-half">
                <label htmlFor="card-cvc-element" className="form-label">
                  CVC
                </label>
                <div className="card-element-wrapper">
                  <CardCvcElement
                    id="card-cvc-element"
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#1a1a1a',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          '::placeholder': {
                            color: '#9ca3af',
                          },
                        },
                        invalid: {
                          color: '#ef4444',
                        },
                      },
                    }}
                    onChange={handleCardCvcChange}
                  />
                </div>
              </div>
            </div>

            <div className="card-element-container">
              <label htmlFor="postal-code" className="form-label">
                Postal Code
              </label>
              <input
                type="text"
                id="postal-code"
                className="postal-code-input"
                placeholder="Enter postal code"
                value={postalCode}
                onChange={handlePostalCodeChange}
                disabled={isLoading}
                data-testid="postal-code-input"
              />
            </div>

            {error && (
              <div className="error-message" data-testid="error-message">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 1L1 14H15L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 6V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="8" cy="11.5" r="0.5" fill="currentColor"/>
                </svg>
                {error}
              </div>
            )}

            {successMessage && (
              <div className="success-message" data-testid="success-message">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 4L6 11.3333L2.66666 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {successMessage}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isLoading}
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!stripe || isLoading || !cardNumberComplete || !cardExpiryComplete || !cardCvcComplete || !postalCode.trim()}
              data-testid="update-payment-btn"
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Updating...
                </>
              ) : (
                'Update Payment Method'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

